import { useMemo } from "react";
import { useTradePathBySupportedTokens } from "@/hooks/tradePathBySupportedTokens/useTradePathBySupportedTokens";

type UseTradePathParams = {
  enabled: boolean;
  accountId?: string;
  exchangeId?: string;
  fromToken: string;
  toToken?: string;
  network?: string;
};

/**
 * Hook to compute trading path for CEX spot trading
 *
 * Determines the optimal multi-hop path to trade between two tokens on a CEX
 * when direct pair may not exist. For example, to trade from DOGE to XRP,
 * it might find the path: DOGE → USDT → XRP.
 *
 * @param params.enabled - Whether to compute the trade path
 * @param params.accountId - The CEX account ID
 * @param params.exchangeId - The exchange ID (e.g., "binance", "kucoin")
 * @param params.fromToken - Source token symbol
 * @param params.toToken - Destination token symbol
 * @param params.network - Network for the trade (optional)
 * @returns Trade path data with intermediate hops, loading state, and supported tokens list
 */
export const useTradePath = ({ enabled, accountId, exchangeId, fromToken, network, toToken }: UseTradePathParams) => {
  const { tradePathBySupportedTokens, error, isLoading, droplistSupportedTokens } = useTradePathBySupportedTokens({
    accountId,
    exchangeId,
    fromToken,
    network,
    enabled,
  });

  const tradePath = useMemo(() => {
    if (!tradePathBySupportedTokens) return null;
    return tradePathBySupportedTokens[toToken ?? ""];
  }, [tradePathBySupportedTokens, toToken, fromToken, network]);

  return {
    tradePath,
    tradePathBySupportedTokens,
    error,
    isLoading,
    droplistDestinationTokens: droplistSupportedTokens,
  };
};
